// Formulário "Nova campanha" da Meta: estado inicial, regras de cada etapa, corpo da requisição
// e textos do resumo. Sem React e sem rede. As regras de objetivo/meta/destino vêm do backend
// (`GET /meta-ads/manage/campaign/options`); aqui só filtramos o que ele devolveu.
// O backend valida tudo de novo: estas checagens existem para o usuário não tropeçar.

import { BUDGET_LABELS, parseAmount } from './metaManage.js'
import { UTM_FIELDS, isEmptyTemplate, templateToForm, validateUtmValue } from './metaUtm.js'

export const STEPS = [
  { id: 'campaign', label: 'Campanha', hint: 'Objetivo e orçamento' },
  { id: 'adset', label: 'Conjunto de anúncios', hint: 'Público, local e lance' },
  { id: 'ad', label: 'Anúncio', hint: 'Mídia, texto e botão' },
  { id: 'review', label: 'Revisão', hint: 'Confira e crie' },
]

export const SPECIAL_CATEGORIES = [
  { value: 'EMPLOYMENT', label: 'Emprego' },
  { value: 'HOUSING', label: 'Moradia' },
  { value: 'CREDIT', label: 'Crédito' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Questões sociais, eleições ou política' },
]

export const BID_STRATEGIES = [
  { value: 'LOWEST_COST_WITHOUT_CAP', label: 'Menor custo', hint: 'A Meta busca o melhor resultado pelo menor custo, sem limite de lance.' },
  { value: 'LOWEST_COST_WITH_BID_CAP', label: 'Limite de lance', hint: 'Você define o lance máximo por leilão.' },
  { value: 'COST_CAP', label: 'Meta de custo', hint: 'Você define o custo médio desejado por resultado.' },
]

export const CAPPED_BIDS = ['LOWEST_COST_WITH_BID_CAP', 'COST_CAP']

export const CONVERSION_EVENTS = [
  { value: 'PURCHASE', label: 'Compra' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'COMPLETE_REGISTRATION', label: 'Cadastro completo' },
  { value: 'ADD_TO_CART', label: 'Adicionar ao carrinho' },
  { value: 'INITIATE_CHECKOUT', label: 'Iniciar finalização de compra' },
  { value: 'CONTACT', label: 'Contato' },
  { value: 'SUBSCRIBE', label: 'Assinatura' },
  { value: 'VIEW_CONTENT', label: 'Visualização de conteúdo' },
  { value: 'OTHER', label: 'Outro' },
]

export const PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'audience_network', label: 'Audience Network' },
  { value: 'messenger', label: 'Messenger' },
]

export const FACEBOOK_POSITIONS = [
  { value: 'feed', label: 'Feed' },
  { value: 'story', label: 'Stories' },
  { value: 'facebook_reels', label: 'Reels' },
  { value: 'video_feeds', label: 'Feed de vídeos' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'right_hand_column', label: 'Coluna da direita' },
  { value: 'search', label: 'Resultados de busca' },
  { value: 'instream_video', label: 'Vídeos in-stream' },
]

export const INSTAGRAM_POSITIONS = [
  { value: 'stream', label: 'Feed' },
  { value: 'story', label: 'Stories' },
  { value: 'reels', label: 'Reels' },
  { value: 'explore', label: 'Explorar' },
  { value: 'explore_home', label: 'Início do Explorar' },
  { value: 'profile_feed', label: 'Feed do perfil' },
]

export const DEVICES = [
  { value: 'mobile', label: 'Celular' },
  { value: 'desktop', label: 'Computador' },
]

export const GENDERS = [
  { value: 'all', label: 'Todos' },
  { value: 'male', label: 'Homens' },
  { value: 'female', label: 'Mulheres' },
]

export const emptyTargeting = () => ({
  countries: [], regions: [], cities: [], ageMin: 18, ageMax: 65, genders: 'all',
  interests: [], customAudiences: [], excludedAudiences: [], advantageAudience: false,
})

export const AD_FORMATS = [
  { value: 'image', label: 'Imagem única', hint: 'Uma imagem JPG ou PNG.' },
  { value: 'video', label: 'Vídeo', hint: 'Um vídeo MP4 ou MOV, com imagem de capa.' },
  { value: 'carousel', label: 'Carrossel', hint: 'De 2 a 10 cartões, cada um com imagem e link.' },
]

export const emptyCard = () => ({ image: null, headline: '', description: '', link: '' })

export const emptyAd = () => ({
  name: '', pageId: '', instagramId: '', format: 'image', image: null, video: null, thumbnail: null,
  cards: [emptyCard(), emptyCard()], message: '', headline: '', description: '', link: '', displayLink: '',
  cta: 'LEARN_MORE', leadFormId: '', utm: { source: '', medium: '', campaign: '', content: '', term: '' },
})

export const emptyForm = (accountId = '') => ({
  accountId,
  campaign: {
    name: '', objective: '', specialCategories: [], specialCountries: [],
    budgetOnCampaign: false, budgetKind: 'daily_budget', amountText: '', spendCapText: '',
  },
  adset: {
    name: '', optimizationGoal: '', destinationType: '', pageId: '', pixelId: '', eventType: 'PURCHASE',
    budgetKind: 'daily_budget', amountText: '', bidStrategy: 'LOWEST_COST_WITHOUT_CAP', bidAmountText: '',
    startTime: '', endTime: '',
    targeting: emptyTargeting(),
    placements: { mode: 'automatic', platforms: ['facebook', 'instagram'], facebook: [], instagram: [], devices: ['mobile', 'desktop'] },
  },
  ad: emptyAd(),
})

// ── Regras do backend ────────────────────────────────────────────────────────

export const objectiveRule = (options, objective) =>
  (options?.objectives || []).find((o) => o.value === objective) || null

export const goalRule = (options, objective, goal) =>
  objectiveRule(options, objective)?.goals.find((g) => g.value === goal) || null

// A mesma meta pode aparecer com vários destinos (ex.: Conversas). Lista de [meta, destino] válidos.
export function goalChoices(options, objective) {
  return (objectiveRule(options, objective)?.goals || []).map((g) => ({
    ...g, key: g.value,
  }))
}

// Ao trocar de objetivo/meta, limpa o que deixou de fazer sentido.
export function normalizeAdset(options, campaign, adset) {
  const goal = goalRule(options, campaign.objective, adset.optimizationGoal)
  if (!goal) return { ...adset, optimizationGoal: '', destinationType: '', pageId: '', pixelId: '' }
  const destinationType = goal.destinations.includes(adset.destinationType) ? adset.destinationType : (goal.destinations[0] || '')
  return {
    ...adset,
    destinationType: destinationType || '',
    pageId: goal.needs_page ? adset.pageId : '',
    pixelId: goal.needs_pixel ? adset.pixelId : '',
  }
}

// ── Validação por etapa ──────────────────────────────────────────────────────

const amountOk = (text) => parseAmount(text) !== null
const dateMs = (value) => (value ? new Date(value).getTime() : null)

export function campaignErrors(form) {
  const c = form.campaign
  const errors = {}
  if (!form.accountId) errors.accountId = 'Escolha a conta de anúncios.'
  if (!c.name.trim()) errors.name = 'Informe o nome da campanha.'
  if (!c.objective) errors.objective = 'Escolha o objetivo.'
  if (c.budgetOnCampaign && !amountOk(c.amountText)) errors.amount = 'Informe o valor do orçamento.'
  if (c.spendCapText.trim() && !amountOk(c.spendCapText)) errors.spendCap = 'Valor inválido.'
  return errors
}

export function adsetErrors(form, options) {
  const { campaign: c, adset: a } = form
  const errors = {}
  const goal = goalRule(options, c.objective, a.optimizationGoal)
  const t = a.targeting
  if (!a.name.trim()) errors.name = 'Informe o nome do conjunto.'
  if (!goal) errors.goal = 'Escolha a meta de otimização.'
  if (goal?.needs_page && !a.pageId) errors.page = 'Escolha a Página do Facebook.'
  if (goal?.needs_pixel && !a.pixelId) errors.pixel = 'Escolha o pixel.'
  if (!c.budgetOnCampaign && !amountOk(a.amountText)) errors.amount = 'Informe o valor do orçamento.'
  if (a.budgetKind === 'lifetime_budget' && !c.budgetOnCampaign && !a.endTime) errors.endTime = 'Orçamento vitalício exige data de término.'
  if (c.budgetOnCampaign && c.budgetKind === 'lifetime_budget' && !a.endTime) errors.endTime = 'Orçamento vitalício exige data de término.'
  if (a.startTime && a.endTime && dateMs(a.endTime) <= dateMs(a.startTime)) errors.endTime = 'O término precisa ser depois do início.'
  if (CAPPED_BIDS.includes(a.bidStrategy) && !amountOk(a.bidAmountText)) errors.bidAmount = 'Informe o valor do lance.'
  if (!(t.countries.length || t.regions.length || t.cities.length)) errors.locations = 'Escolha ao menos um local.'
  if (t.ageMin > t.ageMax) errors.age = 'A idade mínima não pode ser maior que a máxima.'
  if (a.placements.mode === 'manual' && !a.placements.platforms.length) errors.placements = 'Escolha ao menos uma plataforma.'
  if (a.placements.mode === 'manual' && !a.placements.devices.length) errors.devices = 'Escolha ao menos um dispositivo.'
  if (c.specialCategories.length) {
    if (t.interests.length || t.genders !== 'all' || t.ageMin !== 18 || t.ageMax !== 65) {
      errors.special = 'Em categoria especial a Meta não permite filtrar por idade, gênero ou interesses.'
    }
  }
  return errors
}

// Regras do anúncio por destino do conjunto (botões aceitos, link e formulário obrigatórios).
export const adRule = (options, destination) =>
  options?.ad?.destinations?.[destination || ''] || options?.ad?.destinations?.default || { ctas: ['NO_BUTTON'], needs_link: false, needs_form: false }

export const ctaChoices = (options, destination) =>
  adRule(options, destination).ctas.map((value) => ({ value, label: options?.ad?.cta_labels?.[value] || value }))

// Ao entrar na etapa do anúncio: Página do conjunto e botão compatível com o destino.
export function normalizeAd(options, adset, ad) {
  const rule = adRule(options, adset.destinationType)
  return {
    ...ad,
    pageId: ad.pageId || adset.pageId || '',
    cta: rule.ctas.includes(ad.cta) ? ad.cta : rule.ctas[0],
    leadFormId: rule.needs_form ? ad.leadFormId : '',
    // Modelo de UTM do cliente (#318): só pré-preenche se o usuário ainda não digitou nada.
    utm: isEmptyTemplate(ad.utm) && options?.utm_template ? templateToForm(options.utm_template) : ad.utm,
  }
}

const isUrl = (value) => /^https?:\/\/[^\s/]+\S*$/i.test((value || '').trim())

export function adErrors(form, options) {
  const { adset, ad } = form
  const rule = adRule(options, adset.destinationType)
  const errors = {}
  if (!ad.name.trim()) errors.name = 'Informe o nome do anúncio.'
  if (!ad.pageId) errors.page = 'Escolha a Página do anúncio.'
  else if (adset.pageId && ad.pageId !== adset.pageId) errors.page = 'Use a mesma Página promovida no conjunto.'
  if (ad.format === 'image' && !ad.image) errors.media = 'Envie a imagem do anúncio.'
  if (ad.format === 'video') {
    if (!ad.video) errors.media = 'Envie o vídeo do anúncio.'
    if (!ad.thumbnail) errors.thumbnail = 'Envie a imagem de capa do vídeo.'
  }
  if (ad.format === 'carousel') {
    if (ad.cards.length < 2 || ad.cards.length > 10) errors.cards = 'O carrossel precisa de 2 a 10 cartões.'
    ad.cards.forEach((card, i) => {
      if (!card.image) errors[`card${i}`] = `Envie a imagem do cartão ${i + 1}.`
      else if (rule.needs_link && !isUrl(card.link)) errors[`card${i}`] = `Informe um link válido no cartão ${i + 1}.`
    })
  }
  if (ad.format !== 'carousel' && !ad.message.trim()) errors.message = 'Escreva o texto principal.'
  if (rule.needs_link && ad.format !== 'carousel' && !isUrl(ad.link)) errors.link = 'Informe o link de destino (http ou https).'
  if (!rule.needs_link && ad.link.trim() && !isUrl(ad.link)) errors.link = 'O link precisa começar com http:// ou https://.'
  if (rule.needs_form && !ad.leadFormId) errors.leadForm = 'Escolha o formulário de lead.'
  if (!rule.ctas.includes(ad.cta)) errors.cta = 'Esse botão não combina com o destino do conjunto.'
  for (const { key } of UTM_FIELDS) {
    const message = validateUtmValue(ad.utm[key])
    if (message) errors[`utm_${key}`] = message
  }
  return errors
}

export const stepErrors = (stepId, form, options) => {
  if (stepId === 'ad') return adErrors(form, options)
  if (stepId === 'campaign') return campaignErrors(form)
  if (stepId === 'adset') return adsetErrors(form, options)
  return {}
}

export const hasErrors = (errors) => Object.keys(errors).length > 0

// ── Requisição ───────────────────────────────────────────────────────────────

const toIso = (value) => (value ? new Date(value).toISOString() : null)

function adRequest(form) {
  const { ad } = form
  const media = ad.format === 'video' ? ad.video : ad.image
  return {
    name: ad.name.trim(),
    page_id: ad.pageId,
    instagram_user_id: ad.instagramId || null,
    format: ad.format,
    image_hash: ad.format === 'image' ? ad.image?.id ?? null : null,
    video_id: ad.format === 'video' ? ad.video?.id ?? null : null,
    thumbnail_hash: ad.format === 'video' ? ad.thumbnail?.id ?? null : null,
    media_name: ad.format === 'carousel' ? `${ad.cards.length} imagens` : media?.name || '',
    cards: ad.format === 'carousel'
      ? ad.cards.map((c) => ({ image_hash: c.image?.id ?? '', headline: c.headline, description: c.description, link: c.link.trim() }))
      : [],
    message: ad.message.trim(),
    headline: ad.headline.trim(),
    description: ad.description.trim(),
    link: ad.link.trim() || null,
    display_link: ad.displayLink.trim(),
    call_to_action: ad.cta,
    lead_form_id: ad.leadFormId || null,
    utm: ad.utm,
  }
}

export function buildRequest(form, clientId, confirm = false) {
  const { campaign: c, adset: a } = form
  const t = a.targeting
  const p = a.placements
  const manual = p.mode === 'manual'
  return {
    client_id: clientId,
    meta_account_id: String(form.accountId),
    confirm,
    ad: adRequest(form),
    campaign: {
      name: c.name.trim(),
      objective: c.objective,
      special_ad_categories: c.specialCategories,
      special_ad_category_country: c.specialCountries,
      budget_kind: c.budgetOnCampaign ? c.budgetKind : null,
      amount: c.budgetOnCampaign ? parseAmount(c.amountText) : null,
      spend_cap: c.spendCapText.trim() ? parseAmount(c.spendCapText) : null,
    },
    adset: {
      name: a.name.trim(),
      optimization_goal: a.optimizationGoal,
      destination_type: a.destinationType || null,
      page_id: a.pageId || null,
      pixel_id: a.pixelId || null,
      custom_event_type: a.pixelId ? a.eventType : null,
      budget_kind: c.budgetOnCampaign ? null : a.budgetKind,
      amount: c.budgetOnCampaign ? null : parseAmount(a.amountText),
      bid_strategy: a.bidStrategy,
      bid_amount: CAPPED_BIDS.includes(a.bidStrategy) ? parseAmount(a.bidAmountText) : null,
      start_time: toIso(a.startTime),
      end_time: toIso(a.endTime),
      targeting: {
        countries: t.countries.map((c) => c.code),
        regions: t.regions.map((r) => ({ key: r.key, name: r.name })),
        cities: t.cities.map((x) => ({ key: x.key, name: x.name, radius_km: x.radiusKm })),
        age_min: t.ageMin,
        age_max: t.ageMax,
        genders: t.genders,
        interests: t.interests.map((i) => ({ id: i.id, name: i.name })),
        custom_audiences: t.customAudiences.map((x) => ({ id: x.id, name: x.name })),
        excluded_custom_audiences: t.excludedAudiences.map((x) => ({ id: x.id, name: x.name })),
        advantage_audience: t.advantageAudience,
      },
      placements: {
        mode: p.mode,
        publisher_platforms: manual ? p.platforms : [],
        facebook_positions: manual && p.platforms.includes('facebook') ? p.facebook : [],
        instagram_positions: manual && p.platforms.includes('instagram') ? p.instagram : [],
        device_platforms: manual ? p.devices : ['mobile', 'desktop'],
      },
    },
  }
}

// ── Textos do resumo ─────────────────────────────────────────────────────────

const money = (value, currency) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(value || 0)

const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value
const when = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null)
const orNone = (items, none = 'Nenhum') => (items.length ? items.join(', ') : none)

export function budgetText(kind, amount, currency) {
  return kind ? `${money(amount, currency)} (${BUDGET_LABELS[kind]})` : null
}

// Seções do resumo (lateral e etapa de revisão) a partir do que o backend vai enviar (`preview.after`).
export function describeSummary(summary, { options, currency, accountName } = {}) {
  if (!summary?.campaign || !summary?.adset) return []
  const { campaign: c, adset: a } = summary
  const t = a.targeting
  const goal = goalRule(options, c.objective, a.optimization_goal)
  const objective = objectiveRule(options, c.objective)
  const destination = options?.destinations?.[a.destination_type] || a.destination_type
  const pageName = options?.pages?.find((p) => p.id === a.page_id)?.name || a.page_id
  const pixelName = options?.pixels?.find((p) => p.id === a.pixel_id)?.name || a.pixel_id
  const manual = a.placements.mode === 'manual'
  const campaignBudget = budgetText(c.budget_kind, c.amount, currency)
  const adsetBudget = budgetText(a.budget_kind, a.amount, currency)
  const igName = (id) => options?.instagram?.find((x) => x.id === id)?.name || id
  const ad = summary.ad
  const sections = [
    {
      title: 'Campanha',
      rows: [
        ['Conta', accountName || c.account],
        ['Nome', c.name],
        ['Objetivo', objective?.label || c.objective],
        ['Categoria especial', orNone(c.special_ad_categories.map((x) => labelOf(SPECIAL_CATEGORIES, x)), 'Nenhuma')],
        ['Orçamento', campaignBudget ? `${campaignBudget} · na campanha (CBO)` : 'Em cada conjunto de anúncios (ABO)'],
        ['Limite de gasto', c.spend_cap ? money(c.spend_cap, currency) : 'Sem limite'],
        ['Status ao criar', 'Pausada'],
      ],
    },
    {
      title: 'Conjunto de anúncios',
      rows: [
        ['Nome', a.name],
        ['Meta de otimização', goal?.label || a.optimization_goal],
        ['Destino', destination || 'Não se aplica'],
        a.page_id ? ['Página', pageName] : null,
        a.pixel_id ? ['Pixel', `${pixelName} · ${labelOf(CONVERSION_EVENTS, a.custom_event_type)}`] : null,
        adsetBudget ? ['Orçamento', `${adsetBudget} · no conjunto (ABO)`] : null,
        ['Lance', `${labelOf(BID_STRATEGIES, a.bid_strategy)}${a.bid_amount ? ` · ${money(a.bid_amount, currency)}` : ''}`],
        ['Início', when(a.start_time) || 'Assim que for ativado'],
        ['Término', when(a.end_time) || 'Sem data de término'],
        ['Status ao criar', 'Pausado'],
      ].filter(Boolean),
    },
    {
      title: 'Público',
      rows: [
        ['Países', orNone(t.countries, '—')],
        ['Regiões', orNone(t.regions, '—')],
        ['Cidades', orNone(t.cities, '—')],
        ['Idade', `${t.age_min} a ${t.age_max}${t.age_max === 65 ? '+' : ''} anos`],
        ['Gênero', labelOf(GENDERS, t.genders)],
        ['Interesses', orNone(t.interests, 'Nenhum')],
        ['Públicos incluídos', orNone(t.custom_audiences, 'Nenhum')],
        ['Públicos excluídos', orNone(t.excluded_custom_audiences, 'Nenhum')],
        ['Público Advantage+', t.advantage_audience ? 'Ligado' : 'Desligado'],
      ],
    },
    {
      title: 'Posicionamentos',
      rows: manual
        ? [
          ['Modo', 'Manual'],
          ['Plataformas', orNone(a.placements.publisher_platforms.map((x) => labelOf(PLATFORMS, x)))],
          a.placements.facebook_positions.length ? ['Facebook', a.placements.facebook_positions.map((x) => labelOf(FACEBOOK_POSITIONS, x)).join(', ')] : null,
          a.placements.instagram_positions.length ? ['Instagram', a.placements.instagram_positions.map((x) => labelOf(INSTAGRAM_POSITIONS, x)).join(', ')] : null,
          ['Dispositivos', orNone(a.placements.device_platforms.map((x) => labelOf(DEVICES, x)))],
        ].filter(Boolean)
        : [['Modo', 'Automático (Advantage+): a Meta distribui entre todos os posicionamentos']],
    },
  ]
  if (ad) {
    const utm = Object.entries(ad.utm || {}).map(([k, v]) => `${k}=${v}`).join(' · ')
    sections.push({
      title: 'Anúncio',
      rows: [
        ['Nome', ad.name],
        ['Página', options?.pages?.find((p) => p.id === ad.page_id)?.name || ad.page_id],
        ad.instagram_user_id ? ['Instagram', igName(ad.instagram_user_id)] : null,
        ['Formato', labelOf(AD_FORMATS, ad.format)],
        ad.media_name ? ['Mídia', ad.media_name] : null,
        ad.message ? ['Texto principal', ad.message] : null,
        ad.headline ? ['Título', ad.headline] : null,
        ad.description ? ['Descrição', ad.description] : null,
        ad.link ? ['Link de destino', ad.link] : null,
        ad.display_link ? ['Link de exibição', ad.display_link] : null,
        ['Botão', options?.ad?.cta_labels?.[ad.call_to_action] || ad.call_to_action],
        ad.lead_form_id ? ['Formulário de lead', ad.lead_form_id] : null,
        utm ? ['UTM', utm] : null,
        ['Status ao criar', 'Pausado'],
      ].filter(Boolean),
    })
  }
  return sections
}

export const successMessage = (preview) => {
  const after = preview?.after || {}
  const ad = after.ad_id ? ` e anúncio ${after.ad_id}` : ''
  return `Campanha ${after.campaign_id || ''}, conjunto ${after.adset_id || ''}${ad} criados pausados na Meta.`
}

// Resumo "ao vivo" da lateral, no mesmo formato do que o backend devolve na prévia.
export function draftSummary(form) {
  const { campaign: c, adset: a, ad } = form
  const t = a.targeting
  const p = a.placements
  const manual = p.mode === 'manual'
  const adMedia = ad.format === 'video' ? ad.video : ad.image
  return {
    ad: {
      name: ad.name.trim() || '—',
      page_id: ad.pageId,
      instagram_user_id: ad.instagramId || null,
      format: ad.format,
      media_name: ad.format === 'carousel' ? `${ad.cards.length} imagens` : adMedia?.name || '',
      message: ad.message.trim(),
      headline: ad.headline.trim(),
      description: ad.description.trim(),
      link: ad.link.trim(),
      display_link: ad.displayLink.trim(),
      call_to_action: ad.cta,
      lead_form_id: ad.leadFormId || null,
      utm: Object.fromEntries(Object.entries(ad.utm).filter(([, v]) => v.trim())),
    },
    campaign: {
      account: form.accountId,
      name: c.name.trim() || '—',
      objective: c.objective,
      special_ad_categories: c.specialCategories,
      budget_kind: c.budgetOnCampaign ? c.budgetKind : null,
      amount: c.budgetOnCampaign ? parseAmount(c.amountText) : null,
      spend_cap: c.spendCapText.trim() ? parseAmount(c.spendCapText) : null,
    },
    adset: {
      name: a.name.trim() || '—',
      optimization_goal: a.optimizationGoal,
      destination_type: a.destinationType || null,
      page_id: a.pageId || null,
      pixel_id: a.pixelId || null,
      custom_event_type: a.pixelId ? a.eventType : null,
      budget_kind: c.budgetOnCampaign ? null : a.budgetKind,
      amount: c.budgetOnCampaign ? null : parseAmount(a.amountText),
      bid_strategy: a.bidStrategy,
      bid_amount: CAPPED_BIDS.includes(a.bidStrategy) ? parseAmount(a.bidAmountText) : null,
      start_time: toIso(a.startTime),
      end_time: toIso(a.endTime),
      targeting: {
        countries: t.countries.map((x) => x.name || x.code),
        regions: t.regions.map((r) => r.name || r.key),
        cities: t.cities.map((x) => `${x.name || x.key} (+${x.radiusKm} km)`),
        age_min: t.ageMin,
        age_max: t.ageMax,
        genders: t.genders,
        interests: t.interests.map((i) => i.name),
        custom_audiences: t.customAudiences.map((x) => x.name),
        excluded_custom_audiences: t.excludedAudiences.map((x) => x.name),
        advantage_audience: t.advantageAudience,
      },
      placements: {
        mode: p.mode,
        publisher_platforms: manual ? p.platforms : [],
        facebook_positions: manual && p.platforms.includes('facebook') ? p.facebook : [],
        instagram_positions: manual && p.platforms.includes('instagram') ? p.instagram : [],
        device_platforms: manual ? p.devices : [],
      },
    },
  }
}
